import { readTabContext, writeTabContext, clearTabContext } from '@/services/auth/partnerSessionStorage';

const POLICY_CACHE_KEY = 'ewu_device_operational_policy_v1';
const RECENT_POLICY_GRACE_MS = 90000;

let stateReader = () => null;

export class SecondaryOfflineOperationError extends Error {
  constructor(message = 'Los dispositivos secundarios no pueden crear acciones sin conexion.') {
    super(message);
    this.name = 'SecondaryOfflineOperationError';
    this.code = 'SECONDARY_OFFLINE_OPERATION_NOT_ALLOWED';
  }
}

export class LocalHubAuthorityRequiredError extends Error {
  constructor(message = 'La continuidad local todavía no está activa. Confirma el cambio al Local Hub para seguir trabajando sin conexión.') {
    super(message);
    this.name = 'LocalHubAuthorityRequiredError';
    this.code = 'LOCAL_HUB_AUTHORITY_REQUIRED';
  }
}

export function configureDeviceOperationalPolicy(reader) {
  stateReader = typeof reader === 'function' ? reader : () => null;
}

export function readCachedOperationalPolicy() {
  try {
    return JSON.parse(readTabContext(POLICY_CACHE_KEY) || 'null');
  } catch {
    return null;
  }
}

export function clearCachedOperationalPolicy() {
  try {
    clearTabContext(POLICY_CACHE_KEY);
  } catch {
    // The next online policy response remains authoritative.
  }
}

export function persistOperationalPolicy(state) {
  try {
    writeTabContext(POLICY_CACHE_KEY, JSON.stringify({
      secondaryBlockingEnabled: Boolean(state.secondaryBlockingEnabled),
      continuityPolicy: state.continuityPolicy || 'LEGACY_PRIMARY_GUARD',
      restaurantOperationMode: state.restaurantOperationMode || 'CLOUD_MODE',
      registered: Boolean(state.registered),
      isEnabled: Boolean(state.device?.is_enabled),
      isPrimary: Boolean(state.device?.is_primary),
      currentDeviceName: state.device?.name || null,
      canManageDevices: Boolean(state.userIsPartner),
      operationalAccessAllowed: Boolean(state.operationalAccessAllowed),
      operationalAccessReason: state.operationalAccessReason || null,
      operationalAccessCheckedAt: state.operationalAccessCheckedAt || new Date().toISOString(),
    }));
  } catch { /* Runtime state remains authoritative. */ }
}

export function isRecentAllowedOperationalPolicy(cached, now = Date.now()) {
  const checkedAt = Date.parse(cached?.operationalAccessCheckedAt || '');
  return Boolean(
    cached?.operationalAccessAllowed
    && Number.isFinite(checkedAt)
    && now - checkedAt <= RECENT_POLICY_GRACE_MS,
  );
}

export function canCurrentDeviceCreateOfflineActions() {
  const state = stateReader();
  if (!state) return false;
  if (!canUseLegacyBrowserOfflineMode()) return false;
  // Preserve pre-rollout behavior for restaurants where Phase 3 is not enabled.
  if (!state.secondaryBlockingEnabled) return true;
  return Boolean(state.registered && state.device?.is_enabled && state.device?.is_primary);
}

export function canUseLegacyBrowserOfflineMode() {
  const state = stateReader();
  return Boolean(state && state.continuityPolicy === 'LEGACY_PRIMARY_GUARD');
}

export function assertCurrentDeviceCanCreateOfflineActions() {
  if (!canUseLegacyBrowserOfflineMode()) {
    throw new LocalHubAuthorityRequiredError();
  }
  if (!canCurrentDeviceCreateOfflineActions()) {
    throw new SecondaryOfflineOperationError();
  }
  return true;
}

export const OPERATIONAL_BLOCK_CODES = new Set([
  'SECONDARY_DEVICE_BLOCKED',
  'DEVICE_NOT_REGISTERED',
  'DEVICE_REVOKED',
  'NO_PRIMARY_DEVICE',
  'DEVICE_CREDENTIAL_INVALID',
  'DEVICE_POLICY_STATE_UNAVAILABLE',
  'LEGACY_BROWSER_OFFLINE_DISABLED',
  'RESTAURANT_LOCAL_MODE_ACTIVE',
  'RESTAURANT_RECOVERING_TO_CLOUD',
  'RESTAURANT_OPERATION_MODE_CONFLICT',
]);

export function isOperationalBlockingError(error) {
  return Number(error?.response?.status) === 423
    || OPERATIONAL_BLOCK_CODES.has(error?.response?.data?.code);
}

export function hasUsableLocalHubAuthority(rootState) {
  const authority = rootState?.operationalMode;
  const sessionUuid = authority?.session?.session_uuid
    || authority?.session?.id;
  return Boolean(
    authority?.mode === 'LOCAL_MODE'
    && authority?.hubReachable
    && sessionUuid
    && !authority?.commandsFrozen
    && !['LOCAL_HUB_UNAVAILABLE', 'CLOUD_RETURN_PREPARING'].includes(
      authority?.state,
    )
  );
}

export function shouldOverlayOperationalRoute(route) {
  if (!route) return false;
  const allowedNames = new Set([
    'Login', 'LoginAdmin', 'ChangePass', 'Unauthorized',
    'SincronizacionLocal', 'OfflineSectionDisabled', 'MandarIncidencias',
  ]);
  if (allowedNames.has(route.name)) return false;
  return Boolean(route.meta?.partnerMenu || route.meta?.tpvHub);
}
